/* WeQuote Dev Board — staged-delivery editor (v2 §2, verify §69/§73).
 * Lives on the card READ view for pipeline cards: the "Staged delivery"
 * toggle plus inline-editable stage rows using the live click-to-edit
 * affordance — view-only text that becomes a control on click and saves on
 * change/blur (never per keystroke). */
import React, { useState } from 'react'
import { useDevBoard } from '../DevBoardProvider'
import * as ST from '../core/stages'
import * as M from '../core/mutations'
import type { Feature, Stage } from '../core/types'
import { DEVCOLS, DL, TIME_OPTS, timeLabel } from '../core/constants'
import { fmtDate } from '../core/dates'
import { IcCheck } from '../icons'

export const StageEditor: React.FC<{ feat: Feature }> = ({ feat }) => {
  const { mutate, showToast } = useDevBoard()
  const [editKey, setEditKey] = useState<string | null>(null)
  const close = () => setEditKey(null)
  const K = (sid: number, field: string) => sid + ':' + field

  const doneTick = (sg: Stage) => {
    mutate((fs) => ST.markStageDone(fs, feat.id, sg.id))
    if (!sg.done) { // it just became done — prompt when that was the last one
      const after = { ...feat, stages: feat.stages.map((x) => (x.id === sg.id ? { ...x, done: true } : x)) }
      if (ST.allStagesDone(after) && ['testing', 'testcomplete', 'live'].indexOf(feat.status) < 0) {
        setTimeout(() => {
          if (window.confirm('All stages of “' + feat.title + '” are complete.\n\nMove it to Ready to Test?')) {
            mutate((fs) => M.moveStatus(fs, feat.id, 'testing'))
          }
        }, 60)
      }
    }
  }
  const setDeadline = (sg: Stage, val: string) => {
    if (ST.stageDeadlineNeedsPush(feat, val)) {
      const ok = window.confirm('This stage deadline (' + fmtDate(val) + ') is after the card deadline (' + fmtDate(feat.deadline) + ').\n\nOK = move the card deadline out to ' + fmtDate(val) + '\nCancel = keep the card deadline; set the stage deadline earlier')
      if (!ok) { close(); return }
      mutate((fs) => ST.setStageDeadline(fs, feat.id, sg.id, val, true))
    } else {
      mutate((fs) => ST.setStageDeadline(fs, feat.id, sg.id, val, false))
    }
    close()
  }
  const cell = (sg: Stage, field: string, view: React.ReactNode, ctl: React.ReactNode) => (
    editKey === K(sg.id, field)
      ? <span className="ie-ctl">{ctl}</span>
      : <span className="qe-text ie-view" title="Click to edit" onClick={() => setEditKey(K(sg.id, field))}>{view}</span>
  )

  return (
    <div className="dz-group stage-group">
      <div className="dz-hd">Staged delivery</div>
      <label className="rq-design">
        <input type="checkbox" checked={!!feat.multiStage} onChange={(e) => mutate((fs) => ST.setMultiStage(fs, feat.id, e.target.checked))} />
        {' '}Multi-stage development — schedule this big job in stages, each with its own dev, estimate &amp; deadline
      </label>
      {feat.multiStage && (
        <div className="stage-editor">
          <div className="stage-note">Stages run in order — each starts after the one before it finishes (and never double-books a dev). Click any field to edit; a Start pins a stage; the card’s overall estimate is the sum of the stages.</div>
          {feat.stages.map((sg, i) => (
            <div key={sg.id} className={'stage-row' + (sg.done ? ' done' : '')}>
              <span className="stage-n">Stage {i + 1}</span>
              <span className="stage-c stage-c-name">{cell(sg, 'name',
                sg.name || <i className="ie-ph">Name this stage</i>,
                <input autoFocus className="ie-in ie-name" defaultValue={sg.name} placeholder="Stage name"
                  onBlur={(e) => { mutate((fs) => ST.setStageField(fs, feat.id, sg.id, 'name', e.target.value)); close() }}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') close() }} />)}
              </span>
              <span className="stage-c"><span className="stage-lbl">Dev</span>{cell(sg, 'dev',
                sg.dev && DL[sg.dev] ? DL[sg.dev] : <i className="ie-ph">Unassigned</i>,
                <select autoFocus className="ie-in" value={sg.dev} onBlur={close}
                  onChange={(e) => { mutate((fs) => ST.setStageField(fs, feat.id, sg.id, 'dev', e.target.value)); close() }}>
                  <option value="">Unassigned</option>
                  {DEVCOLS.map((d) => <option key={d} value={d}>{DL[d]}</option>)}
                </select>)}
              </span>
              <span className="stage-c"><span className="stage-lbl">Est</span>{cell(sg, 'time',
                sg.time ? timeLabel(sg.time) : <i className="ie-ph">Estimate</i>,
                <select autoFocus className="ie-in" value={sg.time} onBlur={close}
                  onChange={(e) => { mutate((fs) => ST.setStageField(fs, feat.id, sg.id, 'time', e.target.value)); close() }}>
                  {TIME_OPTS.map((o) => <option key={o.val || 'tbd'} value={o.val}>{o.label}</option>)}
                </select>)}
              </span>
              <span className="stage-c"><span className="stage-lbl">Start</span>{cell(sg, 'start',
                sg.start ? fmtDate(sg.start) : <i className="ie-ph">set</i>,
                <input autoFocus type="date" className="ie-in" defaultValue={sg.start} onBlur={close}
                  onChange={(e) => { mutate((fs) => ST.setStageField(fs, feat.id, sg.id, 'start', e.target.value)); close() }} />)}
              </span>
              <span className="stage-c"><span className="stage-lbl">Deadline</span>{cell(sg, 'deadline',
                sg.deadline ? fmtDate(sg.deadline) : <i className="ie-ph">set</i>,
                <input autoFocus type="date" className="ie-in" defaultValue={sg.deadline} onBlur={close}
                  onChange={(e) => setDeadline(sg, e.target.value)} />)}
              </span>
              <span className="stage-proj">{sg.estCompletion ? 'finishes ' + fmtDate(sg.estCompletion) : sg.time ? '—' : ''}</span>
              <button className={'stage-done' + (sg.done ? ' on' : '')} title="Mark this stage complete" onClick={() => doneTick(sg)}><IcCheck size={11} />{sg.done ? '' : ' done'}</button>
              <button className="stage-del" title="Remove stage" onClick={() => { mutate((fs) => ST.removeStage(fs, feat.id, sg.id)); showToast('Stage removed.') }}>×</button>
            </div>
          ))}
          <button className="stage-add" onClick={() => mutate((fs) => ST.addStage(fs, feat.id))}>+ Add stage</button>
        </div>
      )}
    </div>
  )
}
